import { Box, Checkbox, Divider, Input } from "@chakra-ui/react";
import PriceAdjustedByTypeSelect from "@/features/order-sheet-sets/price-adjusted-by-type-select";
import AppButton from "@/features/line-sheet-sets/app-button";
import React, { useCallback, useMemo, useState } from "react";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { produce } from "immer";
import {
  CommissionType,
  PriceAdjustedBy,
} from "@/features/line-sheet-sets/line-sheet-set.types";
import useInflatedTabs from "@/features/line-sheet-sets/helpers/use-inflated-tabs";
import {
  InflatedProduct,
  orderSheetHasDiscountConditionFamily,
} from "@/features/line-sheet-sets/helpers/sheet-state";
import { useRecoilValue } from "recoil";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

interface OrderSheetAdjustPriceFragmentProps {
  orderSheetSetId: number;
  orderSheetId?: number;
  revisionNumber: number;
  tabIndex: number;
  selectedProductIds: number[];
  isOrderSheetSetEditable?: boolean;
  isConfirmedEditable?: boolean;
  // 라인시트세트 수수료 — 컨디션 적용 시 공급가에 반영(IVA 식은 calcPrice가 별도 적용)
  commission?: number | null;
  // 수수료 계산 단위(PRODUCT/ORDER). ORDER면 상품 공급가에는 수수료 미반영.
  commissionType?: CommissionType | null;
}

export default function OrderSheetAdjustPriceFragment({
  orderSheetSetId,
  orderSheetId,
  revisionNumber,
  tabIndex,
  selectedProductIds,
  isOrderSheetSetEditable,
  isConfirmedEditable,
  commission,
  commissionType,
}: OrderSheetAdjustPriceFragmentProps) {
  const { t, tTitle } = useI18nHelper();
  const { warning: showWarning } = useAppToasts();
  const [amount, setAmount] = useState<number>(0);
  const [by, setBy] = useState<PriceAdjustedBy["by"]>("MARKUP");
  // IVA는 DISCOUNT 컨디션에서만 의미. applyIva=false면 미적용, true면 ivaValue 식(기본 /1.22)
  const [applyIva, setApplyIva] = useState<boolean>(true);
  const [ivaValue, setIvaValue] = useState<string>("/1.22");
  const sheetKey = useMemo(() => {
    return {
      orderSheetSetId,
      orderSheetId,
      revisionNumber,
    };
  }, [orderSheetSetId, orderSheetId, revisionNumber]);
  const { getInflatedProductById, updateInflatedProducts } = useInflatedTabs();
  // IVA는 DISCOUNT 컨디션에서만 의미가 있다. 탭에 DISCOUNT 상품이 하나도 없으면
  // 일괄 적용 UI도 통째로 감춘다(그리드의 IVA 컬럼과 같은 기준).
  const hasDiscountCondition = useRecoilValue(
    orderSheetHasDiscountConditionFamily({ key: sheetKey, tabIndex })
  );

  const selectedProducts = useMemo(() => {
    return selectedProductIds
      .map((id) => {
        return getInflatedProductById(sheetKey, tabIndex, id);
      })
      .filter((product): product is InflatedProduct => {
        return product !== undefined;
      });
  }, [sheetKey, tabIndex, selectedProductIds, getInflatedProductById]);

  // 적용 시점에 최신 inflated 상품을 읽는다.
  // (위 selectedProducts는 메모이즈돼 편집 전 서버값으로 stale → 그대로 쓰면 편집한 컨디션이 날아감)
  const getFreshSelectedProducts = useCallback(() => {
    return selectedProductIds
      .map((id) => getInflatedProductById(sheetKey, tabIndex, id))
      .filter((product): product is InflatedProduct => product !== undefined);
  }, [selectedProductIds, getInflatedProductById, sheetKey, tabIndex]);

  const isDisabled = useMemo(() => {
    return (
      (!isOrderSheetSetEditable && !isConfirmedEditable) ||
      selectedProducts.length === 0
    );
  }, [isConfirmedEditable, isOrderSheetSetEditable, selectedProducts.length]);

  const handleApply = useCallback(() => {
    if (amount > 100 || amount < -100) {
      showWarning(
        t("common:apply_price_condition_form.messages.error.out_of_range"),
        "apply_price_condition_form"
      );
      return;
    } else {
      // DISCOUNT일 때만 IVA 적용 여부/계산식을 함께 설정. MARKUP은 IVA 무관(null).
      const ivaFields =
        by === "DISCOUNT"
          ? { applyIva, ivaValue }
          : { applyIva: null, ivaValue: null };
      const nextProducts = produce(getFreshSelectedProducts(), (draft) => {
        for (let i = 0; i < draft.length; i++) {
          if (isOrderSheetSetEditable) {
            draft[i].priceAdjustedBy = {
              by: by,
              value: amount,
              ...ivaFields,
            };
          } else {
            draft[i].confirmedPriceAdjustedBy = {
              by: by,
              value: amount,
              ...ivaFields,
            };
          }
        }
      });
      updateInflatedProducts(sheetKey, tabIndex, nextProducts, {
        commission,
        commissionType,
      });
    }
  }, [
    amount,
    showWarning,
    t,
    by,
    applyIva,
    ivaValue,
    getFreshSelectedProducts,
    updateInflatedProducts,
    sheetKey,
    tabIndex,
    commission,
    commissionType,
    isOrderSheetSetEditable,
  ]);

  // IVA만 별도 적용: 기존 컨디션(by/value)은 유지하고 applyIva/ivaValue만 변경.
  // 개별 셀(OrderSheetIvaCell)과 동일하게 confirmed가 있으면 우선 적용 — 안 그러면
  // 주문 편집 단계에서 일괄 적용이 priceAdjustedBy에 들어가서 confirmedPrice 계산에 반영 안 됨.
  const handleApplyIva = useCallback(() => {
    const nextProducts = produce(getFreshSelectedProducts(), (draft) => {
      for (let i = 0; i < draft.length; i++) {
        const target =
          draft[i].confirmedPriceAdjustedBy ?? draft[i].priceAdjustedBy;
        if (target) {
          target.applyIva = applyIva;
          target.ivaValue = ivaValue;
        }
      }
    });
    updateInflatedProducts(sheetKey, tabIndex, nextProducts, {
      commission,
      commissionType,
    });
  }, [
    getFreshSelectedProducts,
    updateInflatedProducts,
    sheetKey,
    tabIndex,
    commission,
    commissionType,
    applyIva,
    ivaValue,
  ]);

  return (
    <>
      <PriceAdjustedByTypeSelect
        width={"96px"}
        value={by}
        isDisabled={isDisabled}
        onSelect={(v) => {
          if (v) {
            setBy(v);
          }
        }}
      />
      <Input
        width={"60px"}
        textAlign={"center"}
        fontSize={"12px"}
        type={"string"}
        h={"28px"}
        minH={"0"}
        p={"0"}
        borderColor={"#D4D4D1"}
        onChange={(e) => {
          setAmount(Number(e.target.value));
        }}
        disabled={isDisabled}
      />
      <Box fontSize={"12px"}>%</Box>
      <AppButton isDisabled={isDisabled} onClick={handleApply}>
        {tTitle("apply")}
      </AppButton>
      {/* IVA는 컨디션과 별도로 적용 — 기존 by/value는 유지하고 IVA만 변경 */}
      {hasDiscountCondition && (
        <>
          <Divider
            orientation={"vertical"}
            marginX={"5px"}
            borderColor={"#D4D4D1"}
          />
          <Checkbox
            isChecked={applyIva}
            isDisabled={isDisabled}
            onChange={(e) => setApplyIva(e.target.checked)}
          >
            <Box fontSize={"12px"}>IVA</Box>
          </Checkbox>
          <Input
            width={"72px"}
            textAlign={"center"}
            fontSize={"12px"}
            type={"string"}
            h={"28px"}
            minH={"0"}
            p={"0 6px"}
            placeholder={"/1.22"}
            borderColor={"#D4D4D1"}
            value={ivaValue}
            disabled={isDisabled || !applyIva}
            onChange={(e) => setIvaValue(e.target.value)}
          />
          <AppButton isDisabled={isDisabled} onClick={handleApplyIva}>
            {`IVA ${tTitle("apply")}`}
          </AppButton>
        </>
      )}
    </>
  );
}
